import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const features = [
  { icon: 'ri-qr-code-line', titleKey: 'landing.feature_qr_title', descKey: 'landing.feature_qr_desc' },
  { icon: 'ri-file-pdf-2-line', titleKey: 'landing.feature_pdf_title', descKey: 'landing.feature_pdf_desc' },
  { icon: 'ri-pen-nib-line', titleKey: 'landing.feature_sketch_title', descKey: 'landing.feature_sketch_desc' },
  { icon: 'ri-sticky-note-line', titleKey: 'landing.feature_notes_title', descKey: 'landing.feature_notes_desc' },
  { icon: 'ri-share-line', titleKey: 'landing.feature_share_title', descKey: 'landing.feature_share_desc' },
  { icon: 'ri-dashboard-line', titleKey: 'landing.feature_dashboard_title', descKey: 'landing.feature_dashboard_desc' },
];

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGetStarted = () => navigate('/login');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-stone-50">
      <Helmet>
        <title>Qpressen - QR 코드로 프레젠테이션 제어</title>
        <meta name="description" content="스마트폰으로 QR 코드를 스캔하면 바로 원격 제어가 시작됩니다. 별도 앱 설치 없이, 브라우저만으로." />
        <meta property="og:title" content="Qpressen - QR 코드로 프레젠테이션 제어" />
        <meta property="og:description" content="QR 코드 하나로 프레젠테이션을 제어하세요" />
        <meta property="og:image" content="https://qpressen.com/og-image.png" />
        <meta property="og:url" content="https://qpressen.com/landing" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://qpressen.com/landing" />
      </Helmet>
      {/* 네비게이션 바 */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <span className="text-xl font-bold text-slate-900">{t('landing.nav_logo')}</span>
          <button
            onClick={handleGetStarted}
            className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 rounded-lg transition-all"
          >
            {t('landing.nav_cta')}
          </button>
        </div>
      </nav>

      {/* 히어로 섹션 */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 leading-tight whitespace-pre-line mb-6">
            {t('landing.hero_title')}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            {t('landing.hero_subtitle')}
          </p>
          <button
            onClick={handleGetStarted}
            className="px-8 py-3 text-base font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 rounded-xl shadow-lg shadow-violet-200 transition-all hover:shadow-xl hover:shadow-violet-300"
          >
            {t('landing.hero_cta')}
          </button>
        </div>

        {/* 데모 영상 */}
        <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200">
          <video
            src="/demo.mp4"
            poster="/demo-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            className="w-full"
          />
        </div>
      </section>

      {/* 기능 소개 섹션 */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {t('landing.features_title')}
            </h2>
            <p className="text-slate-600">{t('landing.features_subtitle')}</p>
          </div>

          <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
            {features.map((feature) => (
              <div
                key={feature.titleKey}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-xl text-violet-500`}></i>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t(feature.titleKey)}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl px-6 sm:px-12 py-12 sm:py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('landing.cta_title')}
            </h2>
            <p className="text-violet-100 mb-8 max-w-xl mx-auto">
              {t('landing.cta_subtitle')}
            </p>
            <button
              onClick={handleGetStarted}
              className="px-8 py-3 text-base font-semibold text-violet-600 bg-white hover:bg-violet-50 rounded-xl shadow-lg transition-all"
            >
              {t('landing.cta_button')}
            </button>
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">{t('landing.footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
